import { useRef, useState } from "react";
import { GripVertical, ImagePlus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, bestSrc, errText } from "@/lib/api";
import { Progress } from "@/components/ui/progress";

const Thumb = ({ img, i, onAlt, onRemove, onCover, dragProps, isOver }) => (
  <div
    {...dragProps}
    className={`group relative bg-white rounded-xl overflow-hidden border transition-shadow ${isOver ? "ring-2 ring-[#61ce70]" : "border-neutral-200"}`}
    data-testid={`admin-image-${i}`}
  >
    <div className="relative aspect-square bg-neutral-100">
      <img src={bestSrc(img, 600)} alt={img.alt || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <span className="absolute top-2 left-2 bg-black/70 text-white text-xs rounded-full px-2 py-0.5 flex items-center gap-1 cursor-grab">
        <GripVertical size={12} /> {i + 1}
      </span>
      <div className="absolute top-2 right-2 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
        {i > 0 && onCover && (
          <button type="button" onClick={onCover} title="Move to first" className="bg-white/90 hover:bg-white rounded-full p-1.5" data-testid={`admin-image-first-${i}`}>
            <Star size={14} />
          </button>
        )}
        <button type="button" onClick={onRemove} title="Remove" className="bg-white/90 hover:bg-red-600 hover:text-white rounded-full p-1.5" data-testid={`admin-image-remove-${i}`}>
          <Trash2 size={14} />
        </button>
      </div>
      {img.width > 0 && (
        <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[11px] rounded px-1.5">{img.width}×{img.height}</span>
      )}
    </div>
    <input
      value={img.alt || ""}
      onChange={(e) => onAlt(e.target.value)}
      placeholder="Describe image (for Google)"
      className="w-full text-xs px-2 py-2 border-t border-neutral-100 outline-none focus:bg-neutral-50"
      data-testid={`admin-image-alt-${i}`}
    />
  </div>
);

export const ImageManager = ({ images, onChange, uploadPath, max }) => {
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [dropping, setDropping] = useState(false);
  const [progress, setProgress] = useState(null);
  const list = Array.isArray(images) ? images : [];

  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...list];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };

  const upload = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const added = [];
    for (let k = 0; k < arr.length; k++) {
      const fd = new FormData();
      fd.append("files", arr[k]);
      try {
        const { data } = await adminApi.post(uploadPath, fd, {
          onUploadProgress: (e) => setProgress({ n: k + 1, total: arr.length, pct: e.total ? Math.round((e.loaded / e.total) * 100) : 0, name: arr[k].name }),
        });
        added.push(...(Array.isArray(data) ? data : []));
      } catch (ex) {
        toast.error(`${arr[k].name}: ${errText(ex)}`);
      }
    }
    setProgress(null);
    if (added.length) {
      onChange([...list, ...added], true);
      toast.success(`${added.length} image${added.length > 1 ? "s" : ""} added`);
    }
  };

  return (
    <div>
      <div
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("Files")) {
            e.preventDefault();
            setDropping(true);
          }
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          if (!e.dataTransfer.files?.length) return;
          e.preventDefault();
          setDropping(false);
          upload(e.dataTransfer.files);
        }}
        onClick={() => fileRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${dropping ? "border-[#61ce70] bg-[#61ce70]/10" : "border-neutral-300 bg-white hover:border-black"}`}
        data-testid="image-dropzone"
      >
        <ImagePlus className="mx-auto mb-2" size={28} />
        <p className="font-medium">Drop photos here or click to choose</p>
        <p className="text-xs text-neutral-500 mt-1">JPG, PNG, WEBP, GIF or high-res TIFF. Sharp web sizes are made automatically.</p>
        <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.tif,.tiff" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="image-file-input" />
      </div>
      {progress && (
        <div className="mt-3 bg-white rounded-xl p-3" data-testid="upload-progress">
          <p className="text-xs text-neutral-600 mb-2">
            Uploading {progress.n} of {progress.total}: {progress.name} {progress.pct === 100 ? "— processing…" : `${progress.pct}%`}
          </p>
          <Progress value={progress.pct} />
        </div>
      )}
      {max && list.length !== max && <p className="text-xs text-amber-700 mt-3">The home page is designed for {max} images (you have {list.length}).</p>}
      <p className="text-xs text-neutral-500 mt-4 mb-2">Drag photos to reorder. Changes are saved when you press Save.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {list.map((img, i) => (
          <Thumb
            key={img.media_id + "-" + i}
            img={img}
            i={i}
            isOver={over === i && drag !== i}
            onAlt={(alt) => onChange(list.map((x, j) => (j === i ? { ...x, alt } : x)))}
            onRemove={() => onChange(list.filter((_, j) => j !== i))}
            onCover={() => move(i, 0)}
            dragProps={{
              draggable: true,
              onDragStart: (e) => {
                setDrag(i);
                e.dataTransfer.effectAllowed = "move";
              },
              onDragOver: (e) => {
                if (drag == null) return;
                e.preventDefault();
                setOver(i);
              },
              onDrop: (e) => {
                if (drag == null) return;
                e.preventDefault();
                e.stopPropagation();
                move(drag, i);
                setDrag(null);
                setOver(null);
              },
              onDragEnd: () => {
                setDrag(null);
                setOver(null);
              },
            }}
          />
        ))}
      </div>
    </div>
  );
};
